import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const mmAssignmentsAPI = {
  getCCPOAssignments: () => axios.get(`${API_BASE}/api/mm-assignments/company-code-purchasing-org`),
  createCCPOAssignment: (data) => axios.post(`${API_BASE}/api/mm-assignments/company-code-purchasing-org`, data),
  deleteCCPOAssignment: (id) => axios.delete(`${API_BASE}/api/mm-assignments/company-code-purchasing-org/${id}`),
  
  getPlantPOAssignments: () => axios.get(`${API_BASE}/api/mm-assignments/plant-purchasing-org`),
  createPlantPOAssignment: (data) => axios.post(`${API_BASE}/api/mm-assignments/plant-purchasing-org`, data),
  deletePlantPOAssignment: (id) => axios.delete(`${API_BASE}/api/mm-assignments/plant-purchasing-org/${id}`),
  
  getCompanyCodes: () => axios.get(`${API_BASE}/api/company/company-codes`),
  getPlants: () => axios.get(`${API_BASE}/api/material-management/plants`),
  getPurchasingOrgs: () => axios.get(`${API_BASE}/api/purchasing-management/purchasing-organizations`),
};

const MaterialManagementAssignments = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('mm_assignments');
  const [activeTab, setActiveTab] = useState('cc-po');
  const [data, setData] = useState({ ccpoAssignments: [], plantpoAssignments: [] });
  const [companyCodes, setCompanyCodes] = useState([]);
  const [plants, setPlants] = useState([]);
  const [purchasingOrgs, setPurchasingOrgs] = useState([]);
  
  const [selectedCC, setSelectedCC] = useState('');
  const [selectedPlant, setSelectedPlant] = useState('');
  const [selectedPO, setSelectedPO] = useState('');

  useEffect(() => {
    loadData();
    loadForeignKeyData();
  }, []);

  const loadForeignKeyData = async () => {
    try {
      const [cc, pl, po] = await Promise.all([
        mmAssignmentsAPI.getCompanyCodes(),
        mmAssignmentsAPI.getPlants(),
        mmAssignmentsAPI.getPurchasingOrgs(),
      ]);
      setCompanyCodes(cc.data);
      setPlants(pl.data);
      setPurchasingOrgs(po.data);
    } catch (error) {
      console.error('Failed to load foreign key data:', error);
    }
  };

  const loadData = async () => {
    try {
      const [ccpo, plantpo] = await Promise.all([
        mmAssignmentsAPI.getCCPOAssignments(),
        mmAssignmentsAPI.getPlantPOAssignments(),
      ]);
      setData({ ccpoAssignments: ccpo.data, plantpoAssignments: plantpo.data });
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleAddCCPO = async () => {
    if (!selectedCC || !selectedPO) {
      toast.error('Please select both Company Code and Purchasing Organization');
      return;
    }
    try {
      await mmAssignmentsAPI.createCCPOAssignment({ company_code_id: selectedCC, purchasing_org_id: selectedPO });
      setSelectedCC('');
      setSelectedPO('');
      loadData();
      toast.success('Assignment created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleAddPlantPO = async () => {
    if (!selectedPlant || !selectedPO) {
      toast.error('Please select both Plant and Purchasing Organization');
      return;
    }
    try {
      await mmAssignmentsAPI.createPlantPOAssignment({ plant_id: selectedPlant, purchasing_org_id: selectedPO });
      setSelectedPlant('');
      setSelectedPO('');
      loadData();
      toast.success('Assignment created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleDeleteCCPO = async (id) => {
    if (!window.confirm('Delete this assignment?')) return;
    try {
      await mmAssignmentsAPI.deleteCCPOAssignment(id);
      loadData();
      toast.success('Assignment deleted');
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleDeletePlantPO = async (id) => {
    if (!window.confirm('Delete this assignment?')) return;
    try {
      await mmAssignmentsAPI.deletePlantPOAssignment(id);
      loadData();
      toast.success('Assignment deleted');
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const tabs = [
    { id: 'cc-po', label: 'Company Code → Purchasing Org', ratio: '1:N' },
    { id: 'plant-po', label: 'Plant → Purchasing Org', ratio: 'M:N' },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Material Management Assignments</h2>
          <p className="text-sm text-gray-500 mt-1">Manage purchasing organization assignments</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 font-medium flex-1 ${
                  activeTab === tab.id ? 'text-orange-600 border-b-2 border-orange-600' : 'text-gray-600'
                }`}
              >
                <div>{tab.label}</div>
                <div className="text-xs mt-1">
                  <span className={`px-2 py-1 rounded ${activeTab === tab.id ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                    {tab.ratio}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'cc-po' ? (
          <>
            <Card className="p-6 mb-6 bg-orange-50 border-orange-200">
              <h3 className="text-lg font-semibold mb-2">Business Rule (1:N)</h3>
              <p className="text-sm text-gray-700">One Company Code can have multiple Purchasing Orgs (decentralized procurement). However, each Purchasing Org can only be assigned to ONE Company Code.</p>
            </Card>
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Add New Assignment</h3>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-2">Company Code</label>
                  <Select value={selectedCC} onValueChange={setSelectedCC}>
                    <SelectTrigger><SelectValue placeholder="Select Company Code" /></SelectTrigger>
                    <SelectContent>
                      {companyCodes.map(cc => (
                        <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                          {cc.company_code_id} - {cc.company_code_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-2">Purchasing Organization</label>
                  <Select value={selectedPO} onValueChange={setSelectedPO}>
                    <SelectTrigger><SelectValue placeholder="Select Purchasing Org" /></SelectTrigger>
                    <SelectContent>
                      {purchasingOrgs.map(po => (
                        <SelectItem key={po.purchasing_org_id} value={po.purchasing_org_id}>
                          {po.purchasing_org_id} - {po.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {writable && (<Button onClick={handleAddCCPO}><Plus className="w-4 h-4 mr-2" />Add</Button>)}
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Current Assignments</h3>
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Company Code</th>
                    <th className="text-left py-3 px-4">Purchasing Organization</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ccpoAssignments.length === 0 ? (
                    <tr><td colSpan="3" className="text-center py-8 text-gray-500">No assignments</td></tr>
                  ) : (
                    data.ccpoAssignments.map((a) => (
                      <tr key={a.assignment_id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono">{a.company_code_id}</td>
                        <td className="py-3 px-4 font-mono">{a.purchasing_org_id}</td>
                        <td className="py-3 px-4 text-right">
                          {writable && (<Button size="sm" variant="destructive" onClick={() => handleDeleteCCPO(a.assignment_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Card>
          </>
        ) : (
          <>
            <Card className="p-6 mb-6 bg-green-50 border-green-200">
              <h3 className="text-lg font-semibold mb-2">Business Rule (M:N)</h3>
              <p className="text-sm text-gray-700">A Plant may be assigned to one or more Purchasing Orgs, and vice versa. Assignment table tracks the many:many mapping.</p>
            </Card>
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Add New Assignment</h3>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-2">Plant</label>
                  <Select value={selectedPlant} onValueChange={setSelectedPlant}>
                    <SelectTrigger><SelectValue placeholder="Select Plant" /></SelectTrigger>
                    <SelectContent>
                      {plants.map(p => (
                        <SelectItem key={p.plant_id} value={p.plant_id}>
                          {p.plant_id} - {p.plant_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-2">Purchasing Organization</label>
                  <Select value={selectedPO} onValueChange={setSelectedPO}>
                    <SelectTrigger><SelectValue placeholder="Select Purchasing Org" /></SelectTrigger>
                    <SelectContent>
                      {purchasingOrgs.map(po => (
                        <SelectItem key={po.purchasing_org_id} value={po.purchasing_org_id}>
                          {po.purchasing_org_id} - {po.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {writable && (<Button onClick={handleAddPlantPO}><Plus className="w-4 h-4 mr-2" />Add</Button>)}
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Current Assignments</h3>
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Plant</th>
                    <th className="text-left py-3 px-4">Purchasing Organization</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.plantpoAssignments.length === 0 ? (
                    <tr><td colSpan="3" className="text-center py-8 text-gray-500">No assignments</td></tr>
                  ) : (
                    data.plantpoAssignments.map((a) => (
                      <tr key={a.assignment_id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono">{a.plant_id}</td>
                        <td className="py-3 px-4 font-mono">{a.purchasing_org_id}</td>
                        <td className="py-3 px-4 text-right">
                          {writable && (<Button size="sm" variant="destructive" onClick={() => handleDeletePlantPO(a.assignment_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default MaterialManagementAssignments;
